import type { Outcome, TableSummary } from './api'

/** A person as the games need them: just enough to put a name and a face beside a seat. */
export interface GamePerson {
  userId: string
  displayName: string
  photoUrl: string | null
}

/**
 * One colour per seat, always the same, as complete class names so Tailwind can see them. Seat 0 and 1
 * are the two sides of every two-player game, so they are the most different.
 */
export const SEAT = [
  { name: 'Indigo', bg: 'bg-brand', soft: 'bg-brand-soft', text: 'text-brand', ring: 'ring-brand', border: 'border-brand', stroke: 'stroke-brand', fill: 'fill-brand', softFill: 'fill-brand-soft' },
  { name: 'Orange', bg: 'bg-chart-5', soft: 'bg-chart-5/20', text: 'text-chart-5', ring: 'ring-chart-5', border: 'border-chart-5', stroke: 'stroke-chart-5', fill: 'fill-chart-5', softFill: 'fill-chart-5/20' },
  { name: 'Green', bg: 'bg-success', soft: 'bg-success-soft', text: 'text-success', ring: 'ring-success', border: 'border-success', stroke: 'stroke-success', fill: 'fill-success', softFill: 'fill-success-soft' },
  { name: 'Gold', bg: 'bg-warning', soft: 'bg-warning-soft', text: 'text-warning', ring: 'ring-warning', border: 'border-warning', stroke: 'stroke-warning', fill: 'fill-warning', softFill: 'fill-warning-soft' },
  { name: 'Blue', bg: 'bg-chart-4', soft: 'bg-chart-4/20', text: 'text-chart-4', ring: 'ring-chart-4', border: 'border-chart-4', stroke: 'stroke-chart-4', fill: 'fill-chart-4', softFill: 'fill-chart-4/20' },
  { name: 'Violet', bg: 'bg-chart-3', soft: 'bg-chart-3/20', text: 'text-chart-3', ring: 'ring-chart-3', border: 'border-chart-3', stroke: 'stroke-chart-3', fill: 'fill-chart-3', softFill: 'fill-chart-3/20' },
  { name: 'Red', bg: 'bg-destructive', soft: 'bg-destructive/20', text: 'text-destructive', ring: 'ring-destructive', border: 'border-destructive', stroke: 'stroke-destructive', fill: 'fill-destructive', softFill: 'fill-destructive/20' },
  { name: 'Grey', bg: 'bg-text-muted', soft: 'bg-text-muted/20', text: 'text-text-muted', ring: 'ring-text-muted', border: 'border-text-muted', stroke: 'stroke-text-muted', fill: 'fill-text-muted', softFill: 'fill-text-muted/20' },
] as const
export const seatStyle = (seat: number) => SEAT[((seat % SEAT.length) + SEAT.length) % SEAT.length]!

export const firstName = (p: GamePerson | undefined) => (p ? p.displayName.split(' ')[0] || p.displayName : 'Someone')

/** "Rahul", "Rahul and Dev", "Rahul, Dev and Maya". */
export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** The result in a sentence: who won and how, or that it was a draw. */
export function outcomeSentence(outcome: Outcome, players: string[], people: Map<string, GamePerson>, me: string): string {
  const name = (id: string | undefined) => (id === me ? 'You' : firstName(id ? people.get(id) : undefined))
  if (outcome.winners.length === 0) {
    if (outcome.how === 'someone left the table') return 'The game ended: someone left the table.'
    return /^the word was/.test(outcome.how) ? `Nobody got it: ${outcome.how}.` : `A draw (${outcome.how}).`
  }
  const winners = outcome.winners.map((s) => name(players[s]))
  const who = joinNames(winners)
  const verb = winners.length === 1 && winners[0] === 'You' ? 'win' : 'won'
  return `${who} ${verb} (${outcome.how}).`
}

/** Seats still empty, for the lobby. */
export const openSeats = (t: Pick<TableSummary, 'players' | 'max'>) => Math.max(0, t.max - t.players.length)

/** "2 players", "2 to 4 players". */
export const playerRange = (g: { min: number; max: number }) => (g.min === g.max ? `${g.min} players` : `${g.min} to ${g.max} players`)

/** Display cell → board cell, for a viewer whose side is at the top and who sees the board turned round. */
export const turnBoard = (r: number, c: number, size: number, flip: boolean): [number, number] => (flip ? [size - 1 - r, size - 1 - c] : [r, c])

/**
 * Mancala as one viewer sees it: their own pits along the bottom, left to right, store on the right;
 * the other side's along the top, right to left, store on the left. Seeds travel anticlockwise.
 */
export function mancalaLayout(viewerSeat: number): { mine: number[]; theirs: number[]; myStore: number; theirStore: number } {
  const v = viewerSeat === 1 ? 1 : 0
  const own = v === 0 ? [0, 1, 2, 3, 4, 5] : [7, 8, 9, 10, 11, 12]
  const other = v === 0 ? [7, 8, 9, 10, 11, 12] : [0, 1, 2, 3, 4, 5]
  return { mine: own, theirs: [...other].reverse(), myStore: v === 0 ? 6 : 13, theirStore: v === 0 ? 13 : 6 }
}

/* ------------------------------------------------------------------------ drawing */

export interface Stroke {
  c: number
  w: number
  p: number[]
}
export const SKETCH = 1000
/** Brush sizes on the 0..1000 page. */
export const BRUSH = [5, 12, 24, 44] as const
export { INK, PAPER } from '@/lib/sketch-palette'

/** Two points closer than this are the same point, which keeps a held-still pen from piling up coordinates. */
const MIN_STEP = 4
export function appendPoint(points: number[], x: number, y: number): number[] {
  const n = points.length
  if (n >= 2 && Math.hypot(points[n - 2]! - x, points[n - 1]! - y) < MIN_STEP) return points
  return [...points, Math.round(x), Math.round(y)]
}

/** Splits a long line so no single stroke exceeds what the server takes in one piece, sharing the joint. */
export function splitStroke(stroke: Stroke, maxCoords = 500): Stroke[] {
  if (stroke.p.length <= maxCoords) return [stroke]
  const out: Stroke[] = []
  let from = 0
  while (from < stroke.p.length - 2) {
    const to = Math.min(stroke.p.length, from + maxCoords)
    out.push({ ...stroke, p: stroke.p.slice(from, to) })
    from = to - 2
  }
  return out
}

/** A hint like "_ _ _ _" for a word of that length. */
export const blanks = (n: number) => Array.from({ length: n }, () => '_').join(' ')
